import React, { SetStateAction, useEffect, useState, useCallback } from "react";
import { Gateways } from "./Overview/GatewaysSummary";
import { Task, TaskResult, TaskType } from "pages/ProjectManagement/ProjectManagement.types";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import { CustomFieldRender } from "components/helpers/CustomFieldRenderers/CustomFormFields/CustomFieldRender";
import { parseListDate } from "helpers/parseListDate";
import Chevron from "assets/Icon_Btn_07.png";
import * as Yup from "yup";
import api from "api";
import { endpoints } from "config";
import { processErrors } from "helpers/processErrors";
import { customField } from "components/helpers/CustomFieldRenderers/CustomFormFields/model";
import { parseExistingGatewayItemDate } from "helpers/parseGatewayDate";
import DatePicker from "react-datepicker";
import getUserLocale from "get-user-locale";
import calendarIcon from "assets/Icon_Action_06.png";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import { debounce } from "lodash";

import checkIcon from "assets/Icon_Checklist.png";
import { Dropdown } from "components/dropdown/Dropdown";
import { useOptionalGatewayChange } from "../context";

interface Props {
  projectID: number;
  gatewaysID?: number | string;
  gatewayTemplateID: number | string;
  task: Task;
  lastTask?: boolean;
  gatewayIndex: number;
  taskIndex: number;
  gatewayData: Gateways;
  setGatewayData: React.Dispatch<SetStateAction<Gateways | null>>;
  reloadGatewayDataToggle: boolean;
  setReloadGatewayDataToggle: React.Dispatch<SetStateAction<boolean>>;
  nextTaskComplete: boolean;
  toBeExpanded?: boolean;
  backgroundReloadGatewayDataToggle: boolean;
  setBackgroundReloadGatewayDataToggle: React.Dispatch<SetStateAction<boolean>>;
  bEditable: boolean;
}

interface Values {
  customFields: customField[];
  projectsID: number;
  gatewaysID?: number | string | undefined;
  gatewayTemplateID?: number | string | undefined;
  taskTemplatesID: number;
  taskInstancesID: number;
}

const submitForm = async (
  values: Values,
  helpers: FormikProps<any>,
  complete: boolean = false,
  gatewayIndex: number,
  taskIndex: number,
  gatewayData: Gateways,
  setGatewayData: React.Dispatch<SetStateAction<Gateways | null>>,
  setExpand: React.Dispatch<SetStateAction<boolean>>,
  backgroundReloadGatewayDataToggle: boolean,
  setBackgroundReloadGatewayDataToggle: React.Dispatch<SetStateAction<boolean>>,
  onChangeSuccess?: () => void
) => {
  const validate = await helpers.validateForm();

  if (validate && typeof validate === "object" && Object.keys(validate).length === 0) {
    helpers.setSubmitting(true);

    let finalValues: any = { ...values };

    if (complete) {
      finalValues["bComplete"] = true;
    } else {
      finalValues["bComplete"] = false;
    }

    let updatedTask = false;
    let newGatewayData = { ...gatewayData };

    try {
      newGatewayData.gateways[gatewayIndex].tasks[taskIndex] = finalValues;
      updatedTask = true;
    } catch {
      console.log("Failed to update task data: ");
    }

    if (updatedTask) {
      const response = await api.post(
        endpoints.projectmanagement.gatewaysEditTaskData,
        finalValues
      );
      if (response.status === 200 && response.data && response.data.status === `1`) {
        helpers.setSubmitting(false);
        setGatewayData(newGatewayData);
        setExpand(false);

        // Notify that gateway data has changed (for AG Grid refresh on panel close)
        onChangeSuccess?.();

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Success",
                text: `Milestone/task saved and completed`,
              },
            ]}
            className="ToastSuccess"
          />,
          {
            ...toastConfig,
          }
        );
      } else {
        const { errors } = response.data;
        if (errors.length > 0) {
          helpers.setSubmitting(false);
          helpers.setErrors(processErrors(errors));
        }
        toast(
          <ToastMessage
            toastData={[
              {
                title: "Error",
                text: `Failed to save Milestone/task`,
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );
      }
    } else {
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: `Failed to save Milestone/task`,
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }

    // Background refresh gateway data as this can trigger other values elsewhere to update:
    setBackgroundReloadGatewayDataToggle(!backgroundReloadGatewayDataToggle);
    // setReloadGatewayDataToggle(!reloadGatewayDataToggle);
  }
};

import { optionalArray } from "helpers/validation";

const validationSchema = Yup.object().shape({
  customFields: optionalArray(Yup.object()),
});

export const generateInitialValues = (
  projectId: number,
  taskData: Task,
  gatewaysId?: number | string,
  gatewayTemplateId?: number | string
) => {
  return {
    projectsID: projectId,
    gatewaysID: gatewaysId,
    gatewayTemplateID: gatewayTemplateId,
    ...taskData,
    taskTemplateID: taskData.taskTemplatesID,
    customFields: taskData.customFields,
  };
};

export const handleDateChange = async (
  date: Date | null,
  dateType: DateTypes,
  formik: FormikProps<any>,
  gatewayIndex: number,
  taskIndex: number,
  task: Task,
  gatewayData: Gateways,
  setGatewayData: React.Dispatch<SetStateAction<Gateways | null>>,
  projectID: number | null,
  backgroundReloadGatewayDataToggle: boolean,
  setBackgroundReloadGatewayDataToggle: React.Dispatch<SetStateAction<boolean>>,
  gatewaysID?: string | number,
  gatewayTemplateID?: string | number,
  bRemoved?: boolean,
  onChangeSuccess?: () => void
) => {
  let datepickerDate = date;

  let parsedDate: Date | null = null;
  // let parsedDateString: string | null = null;

  let parsedDate2: string | null = null;

  const offset = date ? date.getTimezoneOffset() * 60000 : null;

  if (date && offset !== null) {
    const tempDate = new Date(date.setHours(0, 0, 0));
    // finalDate = new Date(finalDate.setHours(0, 0, 0));
    datepickerDate = tempDate;
    parsedDate = new Date(tempDate.getTime() - offset);
    // parsedDateString = parsedDate.toUTCString();
  }

  if (parsedDate) {
    const year = parsedDate.getUTCFullYear();
    const month = parsedDate.getUTCMonth() + 1;
    const day = parsedDate.getUTCDate();

    const yearString = `${year}`;
    let monthString = `${month}`;
    let dayString = `${day}`;

    if (month < 10) {
      monthString = `0${month}`;
    }

    if (day < 10) {
      dayString = `0${day}`;
    }

    parsedDate2 = `${yearString}-${monthString}-${dayString}T00:00:00`;
  }

  let newTaskData = { ...task };

  let newTaskPayload: any = {
    ...gatewayData.gateways[gatewayIndex].tasks[taskIndex],
  };

  let updatedTask = false;
  let newGatewayData = { ...gatewayData };

  if (formik) {
    if (dateType === DateTypes.ACTUAL) {
      formik.setFieldValue("actualDate", parsedDate2);
      newTaskData.actualDate = parsedDate2;

      try {
        newTaskPayload.actualDate = parsedDate2;
        updatedTask = true;
      } catch {
        console.log("Failed to update task date data: ");
      }
    }
    if (dateType === DateTypes.PLANNED) {
      formik.setFieldValue("plannedDate", parsedDate2);
      newTaskData.plannedDate = parsedDate2;

      try {
        newTaskPayload.plannedDate = parsedDate2;
        updatedTask = true;
      } catch {
        console.log("Failed to update task date data: ");
      }
    }
    if (dateType === DateTypes.FORECAST) {
      formik.setFieldValue("forecastDate", parsedDate2);
      newTaskData.forecastDate = parsedDate2;

      try {
        newTaskPayload.forecastDate = parsedDate2;
        updatedTask = true;
      } catch {
        console.log("Failed to update task date data: ");
      }
    }

    if (task.bComplete && parsedDate2 == null) {
      newTaskPayload.bComplete = false;
    }

    if (updatedTask && newTaskPayload && projectID && (gatewaysID || gatewayTemplateID)) {
      const payload = {
        ...newTaskPayload,
        gatewaysID: gatewaysID,
        gatewayTemplateID: gatewayTemplateID,
        projectsID: projectID,
      };

      const response = await api.post(endpoints.projectmanagement.gatewaysEditTaskData, payload);

      if (response.status === 200 && response.data && response.data.status === `1`) {
        if (response.data.details && typeof response.data.details === "object") {
          const keysToIgnore = ["customFields"];
          let detailsToUpdate: any = {};

          Object.keys(response.data.details).forEach((key) => {
            if (!keysToIgnore.includes(key)) {
              detailsToUpdate[key] = response.data.details[key];
            }
          });

          newGatewayData.gateways[gatewayIndex].tasks[taskIndex] = detailsToUpdate;
        } else {
          newGatewayData.gateways[gatewayIndex].tasks[taskIndex] = newTaskData;
        }

        setGatewayData(newGatewayData);

        // Notify that gateway data has changed (for AG Grid refresh on panel close)
        onChangeSuccess?.();

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Success",
                text: `Milestone/task saved`,
              },
            ]}
            className="ToastSuccess"
          />,
          {
            ...toastConfig,
          }
        );
      } else {
        const { errors } = response.data;
        if (errors.length > 0) {
        }

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Error",
                text: `Failed to save Milestone/task`,
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );
      }
    } else {
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: `Failed to save Milestone/task`,
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }

    // Background refresh gateway data as this can trigger other values elsewhere to update:
    setBackgroundReloadGatewayDataToggle(!backgroundReloadGatewayDataToggle);
  }
};

export const debouncedHandleDateChange = debounce(
  (
    e,
    DateTypes,
    formik,
    gatewayIndex,
    taskIndex,
    task,
    gatewayData,
    setGatewayData,
    gatewaysID,
    gatewayTemplateID,
    projectID,
    backgroundReloadGatewayDataToggle,
    setBackgroundReloadGatewayDataToggle,
    onChangeSuccess?: () => void
  ) =>
    handleDateChange(
      e,
      DateTypes,
      formik,
      gatewayIndex,
      taskIndex,
      task,
      gatewayData,
      setGatewayData,
      projectID,
      backgroundReloadGatewayDataToggle,
      setBackgroundReloadGatewayDataToggle,
      gatewaysID,
      gatewayTemplateID,
      undefined,
      onChangeSuccess
    ),
  2000 // Wait 2 seconds before procesing date change
);

export enum DateTypes {
  "PLANNED" = "PLANNED",
  "FORECAST" = "FORECAST",
  "ACTUAL" = "ACTUAL",
}

const TaskForm: React.FC<Props> = ({
  projectID,
  gatewaysID,
  gatewayTemplateID,
  task,
  lastTask,
  gatewayIndex,
  taskIndex,
  reloadGatewayDataToggle,
  gatewayData,
  setGatewayData,
  setReloadGatewayDataToggle,
  nextTaskComplete,
  toBeExpanded,
  backgroundReloadGatewayDataToggle,
  setBackgroundReloadGatewayDataToggle,
  bEditable,
}) => {
  const [parsedPlannedDate, setParsedPlannedDate] = useState<Date | null>(null);
  const [parsedForecastDate, setParsedForecastDate] = useState<Date | null>(null);
  const [parsedActualDate, setParsedActualDate] = useState<Date | null>(null);
  const [expand, setExpand] = useState<boolean>(false);

  // Gateway change tracking for AG Grid refresh on panel close
  const gatewayChange = useOptionalGatewayChange();
  const handleChangeSuccess = useCallback(() => {
    gatewayChange?.setHasChanges(true);
  }, [gatewayChange]);

  const [singleSelectValue, setSingleSelectValue] = useState<{
    label: string;
    value: TaskResult | null;
  } | null>(null);

  const singleSelectOptions = [
    {
      label: "Yes",
      value: TaskResult.YES,
    },
    {
      label: "No",
      value: TaskResult.NO,
    },
    {
      label: "N/A",
      value: TaskResult.N_A,
    },
  ];

  // Add in when endpoint is available with the value:
  useEffect(() => {
    if (task.taskType === TaskType.STANDARD_TASK) {
      switch (task.taskResult) {
        case TaskResult.N_A:
          setSingleSelectValue({ label: "N/A", value: TaskResult.N_A });
          break;
        case TaskResult.NO:
          setSingleSelectValue({ label: "No", value: TaskResult.NO });
          break;

        case TaskResult.YES:
          setSingleSelectValue({ label: "Yes", value: TaskResult.YES });
          break;
      }
    }
  }, [task.bComplete, task.taskResult]);

  useEffect(() => {
    if (task.forecastDate) {
      setParsedForecastDate(parseExistingGatewayItemDate(task.forecastDate));
    } else {
      setParsedForecastDate(null);
    }
  }, [task.forecastDate]);

  useEffect(() => {
    if (task.plannedDate) {
      setParsedPlannedDate(parseExistingGatewayItemDate(task.plannedDate));
    } else {
      setParsedPlannedDate(null);
    }
  }, [task.plannedDate]);

  useEffect(() => {
    if (task.actualDate) {
      setParsedActualDate(parseExistingGatewayItemDate(task.actualDate));
    } else {
      setParsedActualDate(null);
    }
  }, [task.actualDate]);

  const locale = getUserLocale();

  useEffect(() => {
    if (toBeExpanded && !task.bComplete) setExpand(true);
    if (expand && !toBeExpanded && task.bComplete) setExpand(false);
    if (!task.bComplete) setExpand(true);
  }, [toBeExpanded, task.bComplete]);

  const handleSingleSelectOnChange = async (
    value: {
      label: string;
      value: TaskResult | null;
    },
    formik: FormikProps<any>,
    backgroundReloadGatewayDataToggle: boolean
  ) => {
    setSingleSelectValue(value);

    const bTaskCleared = value.label == "";
    const payloadValue = value.value;

    // TODO:
    // Update the value in the data to send to the endpoint

    const payload = {
      ...gatewayData.gateways[gatewayIndex].tasks[taskIndex],
      taskResult: payloadValue,
      projectsID: projectID,
      gatewaysID: gatewaysID,
      gatewayTemplateID: gatewayTemplateID,
      bComplete: bTaskCleared ? false : true,
      completedBy: bTaskCleared ? null : task.completedBy,
    };

    if (formik) {
      formik.setFieldValue("taskResult", payloadValue);
    }

    const response = await api.post(endpoints.projectmanagement.gatewaysEditTaskData, payload);

    let newGatewayData = { ...gatewayData };

    let newTaskData = {
      ...payload,
      taskResult: payloadValue,
      bComplete: bTaskCleared ? false : true,
    };

    if (response.status === 200 && response.data && response.data.status === `1`) {
      newGatewayData.gateways[gatewayIndex].tasks[taskIndex] = newTaskData;
      setGatewayData(newGatewayData);

      setBackgroundReloadGatewayDataToggle(!backgroundReloadGatewayDataToggle);

      // Notify that gateway data has changed (for AG Grid refresh on panel close)
      handleChangeSuccess();

      toast(
        <ToastMessage
          toastData={[
            {
              title: "Success",
              text: `Milestone/task saved`,
            },
          ]}
          className="ToastSuccess"
        />,
        {
          ...toastConfig,
        }
      );
    } else {
      const { errors } = response.data;
      if (errors.length > 0) {
      }

      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: `Failed to save Milestone/task`,
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }
  };
  return (
    <Formik
      initialValues={generateInitialValues(
        projectID as number,
        task,
        gatewaysID as number,
        gatewayTemplateID as number
      )}
      onSubmit={() => {}} // Submit function is above
      validationSchema={validationSchema}
    >
      {(formik) => {
        let parsedCompletedDate = task.completedDate
          ? `${parseListDate(task.completedDate, true) ?? ""}`
          : "";

        const splitDate = parsedCompletedDate.split(":");

        let dateAndTime = false;

        if (splitDate.length === 4) {
          parsedCompletedDate = `${splitDate[0]}    ${splitDate[1]}:${splitDate[2]}:${splitDate[3]}`;

          dateAndTime = true;
        }

        const showButtonPlaceholder =
          (formik.values &&
            typeof formik.values === "object" &&
            formik.values.hasOwnProperty("customFields") &&
            Array.isArray(formik.values.customFields) &&
            formik.values.customFields.length === 0) ||
          !formik.values ||
          (formik.values && !formik.values.customFields) ||
          task.taskType !== TaskType.CUSTOM_TASK;

        if (task.bHeaderTask) return null;

        return (
          <Form
            className={`TaskField ${lastTask ? "LastTask" : ""}  ${
              expand ? "Expanded" : ""
            } ${task.bHeaderTask ? "HeaderTaskMilestone" : ""}`}
          >
            <div className={`Cirle ${task.bComplete ? "True" : ""}`}></div>

            <div
              className={`Line ${expand ? "Expanded" : ""} ${nextTaskComplete ? "Complete" : ""}`}
            ></div>

            <div
              className={`TaskHeader ${expand ? "Expanded" : ""} ${
                task.bActualActive || task.bForecastActive || task.bPlannedActive
                  ? "DatesAvailable"
                  : "NoDates"
              } ${task.bComplete ? "Complete" : ""} ${
                !task.completedBy ? "NoCompletedBy" : ""
              } ${!parsedCompletedDate ? "NoCompletedDate" : ""} ${
                task.taskType === TaskType.STANDARD_TASK ? "ShowSingleSelect" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setExpand(!expand);
              }}
            >
              <div className="TaskName">
                <div className="Text">{task.taskName}</div>

                <div className={`Dates ${task.bComplete ? "Complete" : ""}`}>
                  {task.bPlannedActive ? (
                    <div className="RequiredDate DateContainer">
                      <div
                        className={`Date ${task.bPlannedReadOnly || !bEditable ? "Disabled" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="Text">Planned:</div>

                        <DatePicker
                          dateFormat="dd-MMM-yyyy"
                          locale={locale}
                          onChange={(e) =>
                            debouncedHandleDateChange(
                              e,
                              DateTypes.PLANNED,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              gatewaysID,
                              gatewayTemplateID,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              handleChangeSuccess
                            )
                          }
                          selected={parsedPlannedDate ?? null}
                        />
                        {(task.plannedDate || task.bComplete) &&
                          removeDate(() =>
                            handleDateChange(
                              null,
                              DateTypes.PLANNED,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              gatewaysID,
                              gatewayTemplateID,
                              undefined,
                              handleChangeSuccess
                            )
                          )}
                        <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
                      </div>
                    </div>
                  ) : (
                    <div className="RequiredDate DateContainer"></div>
                  )}

                  {task.bForecastActive ? (
                    <div className="ProjectedDate DateContainer">
                      <div
                        className={`Date ${task.bForecastReadOnly || !bEditable ? "Disabled" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="Text">Forecast:</div>

                        <DatePicker
                          dateFormat="dd-MMM-yyyy"
                          locale={locale}
                          onChange={(e) =>
                            debouncedHandleDateChange(
                              e,
                              DateTypes.FORECAST,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              gatewaysID,
                              gatewayTemplateID,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              handleChangeSuccess
                            )
                          }
                          selected={parsedForecastDate ?? null}
                        />
                        {(task.forecastDate || task.bComplete) &&
                          removeDate(() =>
                            handleDateChange(
                              null,
                              DateTypes.FORECAST,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              gatewaysID,
                              gatewayTemplateID,
                              undefined,
                              handleChangeSuccess
                            )
                          )}
                        <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
                      </div>
                    </div>
                  ) : (
                    <div className="ProjectedDate DateContainer"></div>
                  )}

                  {task.bActualActive ? (
                    <div className={`CompletionDate DateContainer `}>
                      <div
                        className={`Date ${task.bActualReadOnly || !bEditable ? "Disabled" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="Text">Actual:</div>

                        <DatePicker
                          dateFormat="dd-MMM-yyyy"
                          locale={locale}
                          className={task.bActualReadOnly ? "locked" : ""}
                          onChange={(e) =>
                            debouncedHandleDateChange(
                              e,
                              DateTypes.ACTUAL,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              gatewaysID,
                              gatewayTemplateID,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              handleChangeSuccess
                            )
                          }
                          selected={parsedActualDate ?? null}
                        />
                        {(task.actualDate || task.bComplete) &&
                          removeDate(() =>
                            handleDateChange(
                              null,
                              DateTypes.ACTUAL,
                              formik,
                              gatewayIndex,
                              taskIndex,
                              task,
                              gatewayData,
                              setGatewayData,
                              projectID,
                              backgroundReloadGatewayDataToggle,
                              setBackgroundReloadGatewayDataToggle,
                              gatewaysID,
                              gatewayTemplateID,
                              undefined,
                              handleChangeSuccess
                            )
                          )}
                        <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
                      </div>
                    </div>
                  ) : (
                    <div className="CompletionDate DateContainer"></div>
                  )}
                </div>

                {task.taskType === TaskType.STANDARD_TASK && (
                  <div
                    className={`GatewaysTabSingleSelectContainer ${!bEditable && "Disabled"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Dropdown
                      options={singleSelectOptions}
                      value={singleSelectValue}
                      onChange={(selectedOption) => {
                        handleSingleSelectOnChange(
                          { label: selectedOption?.label ?? "", value: selectedOption?.value },
                          formik,
                          backgroundReloadGatewayDataToggle
                        );
                      }}
                      placeholder=""
                      isClearable={true}
                    />
                  </div>
                )}
              </div>
              <div className={`ExtraDetails `}>
                {task.bComplete ? (
                  <div className="Completed">
                    <div className="IconContainer">
                      <img src={checkIcon} alt="Completed" className="Icon" />
                    </div>
                  </div>
                ) : (
                  <div className="NotCompletedPlaceholder"></div>
                )}
                <img
                  onClick={() => setExpand(!expand)}
                  className={`Chevron ${expand ? "ChevronExpand" : ""}`}
                  src={Chevron}
                />
              </div>
            </div>

            {expand && (
              <>
                {/* <div className="Placeholder"></div> */}

                {/* Placeholder: */}
                <div></div>
                <div
                  className="TaskForm"
                  onClick={(e) => {
                    setExpand(!expand);
                    e.stopPropagation();
                  }}
                >
                  {task.customFields &&
                    task.customFields.length > 0 &&
                    (task.taskType === TaskType.CUSTOM_TASK ||
                      task.taskType === TaskType.MILESTONE) && (
                      <CustomFieldRender
                        field={{ name: "customFields" }}
                        readOnly={false}
                        classWrapper={"GatewayTab"}
                      />
                    )}

                  {/* Placeholder for grid and positioning button in bottom right */}
                  {showButtonPlaceholder && <div className="ButtonPlaceholder"></div>}

                  {/* {(formik.values &&
                    typeof formik.values === "object" &&
                    formik.values.hasOwnProperty("customFields") &&
                    Array.isArray(formik.values.customFields) &&
                    (formik.values.customFields.length % 2 === 0 ||
                      formik.values.customFields.length === 0)) ||
                    !formik.values ||
                    (formik.values && !formik.values.customFields && (
                      <div className="ButtonPlaceholder"></div>
                    ))} */}

                  {/* Just save: */}
                  {/* <button
                    className="Btn Small SaveTaskDetails"
                    onClick={() =>
                      submitForm(
                        formik.values,
                        formik,
                        false,
                        reloadGatewayDataToggle,
                        setReloadGatewayDataToggle
                      )
                    }
                  >
                    Save
                  </button> */}

                  {/* Save and complete */}
                  <div className="ButtonContainer">
                    {task.completedDate && (
                      <>
                        <div></div>
                        <div className="CompletedBy">
                          <div className="Text">Completed: </div>

                          <div
                            className={`CompletedDate ${
                              dateAndTime && splitDate ? "TimeAndDate" : ""
                            }`}
                          >
                            {/* <div className="Text">{task.completedBy}</div> */}
                            {parsedCompletedDate && (
                              <div className="ParsedDate">
                                {dateAndTime && splitDate && (
                                  <>
                                    <div className="Text">{splitDate[0]}</div>
                                    <div className="Text">{`${splitDate[1]}:${splitDate[2]}:${splitDate[3]}`}</div>
                                  </>
                                )}

                                {!dateAndTime && <div className="Text">{parsedCompletedDate}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    <button
                      className="Btn Small SaveTaskDetails"
                      type="submit"
                      onClick={(e) => {
                        e.stopPropagation();
                        submitForm(
                          formik.values,
                          formik,
                          true,
                          gatewayIndex,
                          taskIndex,
                          gatewayData,
                          setGatewayData,
                          setExpand,
                          backgroundReloadGatewayDataToggle,
                          setBackgroundReloadGatewayDataToggle,
                          handleChangeSuccess
                        );
                      }}
                    >
                      {task.bComplete ? "Save & edit" : "Save & complete"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </Form>
        );
      }}
    </Formik>
    // </div>
  );
};

export default TaskForm;

export const removeDate = (handleClick: () => void) => {
  return (
    <div
      className="RemoveIcon"
      onClick={() => {
        handleClick();
      }}
    >
      <svg
        height="14"
        width="14"
        viewBox="0 0 20 20"
        aria-hidden="true"
        style={{ fill: "#58595b" }}
        focusable="false"
      >
        <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
      </svg>
    </div>
  );
};
